/**
 * Command surface. UI and effects dispatch only these.
 * A new product rule is a new command + reducer case + selector test — not a new useState.
 *
 * Invariants (enforced in the reducer):
 * - ApprovePages requires job === "idle", a fetched document, and chosen pages
 *   (scanned/upload must have selected pages; newer digital rows are the whole document
 *   and are accepted by default without storing a Document annotation).
 * - RejectPages (Ignore) requires idle or saving and does not need a PDF.
 * - SelectPages that changes the set unchecks review and marks analysis stale; identical set is a no-op.
 * - ContentSucceeded never writes review.state. A new AI result is a field edit:
 *   keep saved annotations and mark overrides so Reset can restore them.
 * - PaataleResolved is the same for entitledParty only: keep the saved note, show
 *   the fetched current-party text, and let Reset restore the annotation.
 * - ResetField clears that field’s override; it does not delete the annotation.
 * - RevertPages clears document annotation + four field notes; keeps stedfæstelse; may request a latent land-registry AKT fetch.
 * - AcceptField writes only that field’s note id/text.
 * - SelectAkt applies only after a real akt PDF was fetched; a miss keeps the previous row.
 * - UploadPdf resets analysis, fields, and review like SelectAkt; no pages are chosen.
 *   The upload effect then accepts that row, same as Godkend.
 * - A persisted Document annotation is an A approval unless it is rejected.
 * - Locate on a custom upload updates aiSuggested; it does not invent “entire file” as AI pages.
 */
import type { ANNAnnotationRecord } from "src/modules/ANNModule/ANNTypes";
import type { LLMAnalysisResultItem, LLMMetadata } from "src/modules/ANNModule/ANNLLMAnalyzer";
import type { ServitutAnalysisFieldKey } from "src/modules/ANNModule/ANNServitutFieldAnnotations";
import type { ServitutRedegoerelsePhase } from "../model/report";
import type { ServitutAiFieldStatus } from "src/modules/ANNModule/ANNServitutProvenance";
import type {
   ServitutAktSearchHint,
   ServitutExportProgress,
   ServitutLoadProgress,
   ServitutMatrixColumnDetail,
} from "../model/report";
import type { DocumentBinding, ServitutPdfStatus, ServitutReviewOrigin, ServitutRow } from "../model/row";

export enum ServitutReportActionType {
   LoadStarted = "LoadStarted",
   LoadProgress = "LoadProgress",
   LoadFailed = "LoadFailed",
   ReportPrepared = "ReportPrepared",
   AnnHydrated = "AnnHydrated",
   AnnRecordsChanged = "AnnRecordsChanged",
   BindLandRegistry = "BindLandRegistry",
   SelectAkt = "SelectAkt",
   FetchAktStarted = "FetchAktStarted",
   FetchAktFailed = "FetchAktFailed",
   AktFetchBatchStarted = "AktFetchBatchStarted",
   AktFetchBatchStopped = "AktFetchBatchStopped",
   UploadPdf = "UploadPdf",
   LocateStarted = "LocateStarted",
   LocateSucceeded = "LocateSucceeded",
   LocateFailed = "LocateFailed",
   SelectPages = "SelectPages",
   ApprovePages = "ApprovePages",
   RejectPages = "RejectPages",
   RevertPages = "RevertPages",
   ContentStarted = "ContentStarted",
   ContentSucceeded = "ContentSucceeded",
   ContentFailed = "ContentFailed",
   PaataleResolved = "PaataleResolved",
   AcceptField = "AcceptField",
   RevertField = "RevertField",
   ResetField = "ResetField",
   SetPhase = "SetPhase",
   SetCaseDescription = "SetCaseDescription",
   SetAnalysisField = "SetAnalysisField",
   CommitStarted = "CommitStarted",
   CommitSucceeded = "CommitSucceeded",
   CommitFailed = "CommitFailed",
   SetOpeningRow = "SetOpeningRow",
   SetExporting = "SetExporting",
   SetResolvingPaatale = "SetResolvingPaatale",
   AttachStedfaestelse = "AttachStedfaestelse",
   MarkUnsaved = "MarkUnsaved",
   MarkSaved = "MarkSaved",
}

export type ReportPreparedPayload = {
   rows: ServitutRow[];
   columns: number[];
   columnLabels: Record<string, string>;
   columnDetails: Record<string, ServitutMatrixColumnDetail>;
   uniqueAktCount: number;
   metadataById: Record<string, LLMMetadata>;
   aktSearchByBfe: Record<string, ServitutAktSearchHint>;
   caseDescription?: string;
};

export type LocateRowResult = {
   rowId: string;
   item: LLMAnalysisResultItem;
};

export type ContentRowResult = {
   rowId: string;
   item: LLMAnalysisResultItem;
   fieldStatuses: Record<ServitutAnalysisFieldKey, ServitutAiFieldStatus>;
   fieldTexts: Record<ServitutAnalysisFieldKey, string>;
};

export type BindLandRegistryPayload = {
   rowId: string;
   document: DocumentBinding;
   pdfStatus: ServitutPdfStatus;
   sourceMessage?: string;
   metadata?: LLMMetadata;
};

export type ServitutReportAction =
   | { type: ServitutReportActionType.LoadStarted; payload?: ServitutLoadProgress }
   | { type: ServitutReportActionType.LoadProgress; payload: ServitutLoadProgress }
   | { type: ServitutReportActionType.LoadFailed; payload: string }
   | { type: ServitutReportActionType.ReportPrepared; payload: ReportPreparedPayload }
   | { type: ServitutReportActionType.AnnHydrated; payload: { rows: ServitutRow[] } }
   | { type: ServitutReportActionType.AnnRecordsChanged; payload: { records: ANNAnnotationRecord[] } }
   | { type: ServitutReportActionType.BindLandRegistry; payload: BindLandRegistryPayload }
   | { type: ServitutReportActionType.SelectAkt; payload: BindLandRegistryPayload }
   | { type: ServitutReportActionType.FetchAktStarted; payload: { rowIds: string[] } }
   | { type: ServitutReportActionType.FetchAktFailed; payload: { rowIds: string[] } }
   | { type: ServitutReportActionType.AktFetchBatchStarted; payload: { rowIds: string[] } }
   | { type: ServitutReportActionType.AktFetchBatchStopped; payload?: { rowIds?: string[] } }
   | { type: ServitutReportActionType.UploadPdf; payload: { rowId: string; s3Key: string; filename: string; selectedPages: number[]; metadata?: LLMMetadata } }
   | { type: ServitutReportActionType.LocateStarted; payload: { rowIds: string[] } }
   | { type: ServitutReportActionType.LocateSucceeded; payload: { results: LocateRowResult[] } }
   | { type: ServitutReportActionType.LocateFailed; payload: { rowIds: string[] } }
   | { type: ServitutReportActionType.SelectPages; payload: { rowId: string; aktPages: number[] } }
   | { type: ServitutReportActionType.ApprovePages; payload: { rowId: string; origin: ServitutReviewOrigin } }
   | { type: ServitutReportActionType.RejectPages; payload: { rowId: string; origin: ServitutReviewOrigin } }
   | { type: ServitutReportActionType.RevertPages; payload: { rowId: string } }
   | { type: ServitutReportActionType.ContentStarted; payload: { rowIds: string[] } }
   | { type: ServitutReportActionType.ContentSucceeded; payload: { results: ContentRowResult[] } }
   | { type: ServitutReportActionType.ContentFailed; payload: { rowIds: string[] } }
   | { type: ServitutReportActionType.PaataleResolved; payload: { rowId: string; item: LLMAnalysisResultItem } }
   | { type: ServitutReportActionType.AcceptField; payload: { rowId: string; field: ServitutAnalysisFieldKey; noteId: string; text: string } }
   | { type: ServitutReportActionType.RevertField; payload: { rowId: string; field: ServitutAnalysisFieldKey; restoredStatus?: ServitutAiFieldStatus } }
   | { type: ServitutReportActionType.ResetField; payload: { rowId: string; field: ServitutAnalysisFieldKey } }
   | { type: ServitutReportActionType.SetPhase; payload: ServitutRedegoerelsePhase }
   | { type: ServitutReportActionType.SetCaseDescription; payload: string }
   | { type: ServitutReportActionType.SetAnalysisField; payload: ServitutAnalysisFieldKey }
   | { type: ServitutReportActionType.CommitStarted; payload: { rowIds: string[] } }
   | { type: ServitutReportActionType.CommitSucceeded; payload: { records: ANNAnnotationRecord[]; rowIds?: string[]; deletedIds?: string[] } }
   | { type: ServitutReportActionType.CommitFailed; payload: { rowIds: string[] } }
   | { type: ServitutReportActionType.SetOpeningRow; payload: string | null }
   | { type: ServitutReportActionType.SetExporting; payload: { exporting: boolean; progress: ServitutExportProgress | null } }
   | { type: ServitutReportActionType.SetResolvingPaatale; payload: { rowId: string; resolving: boolean } }
   | { type: ServitutReportActionType.AttachStedfaestelse; payload: { rowId: string; stedfaestelseId: string } }
   | { type: ServitutReportActionType.MarkUnsaved; payload: { rowIds: string[] } }
   | { type: ServitutReportActionType.MarkSaved; payload: { rowIds: string[] } };
